import React, { createContext, useContext, useState, useEffect } from 'react';
import { onAuthStateChanged, createUserWithEmailAndPassword, signInWithEmailAndPassword, signOut } from 'firebase/auth';
import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import { auth, db } from '../config/firebaseConfig';

// Create the auth context
const AuthContext = createContext();

// Custom hook to use the auth context
export const useAuth = () => useContext(AuthContext);

// Provider component
export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Listen for auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      
      if (user) {
        try {
          // Get user data from Firestore
          const userDoc = await getDoc(doc(db, 'users', user.uid));
          if (userDoc.exists()) {
            const data = userDoc.data();
            setUserData(data);
            
            // If user exists but doesn't have isAffiliate flag, they're from main site
            // Don't auto-set the flag - they need to sign up through affiliate portal
          } else {
            // User authenticated but no document - will be created on signup
            setUserData(null);
          }
        } catch (error) {
          console.error('Error fetching user data:', error);
        }
      } else {
        setUserData(null);
      }
      
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  // Sign up new affiliate user
  const signUp = async (email, password, firstName, lastName) => {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;
    
    // Create user document with affiliate flag
    await setDoc(doc(db, 'users', user.uid), {
      email,
      firstName,
      lastName,
      userType: 'user',
      isAffiliate: true,
      affiliateStatus: null, // null = not applied, 'pending', 'approved', 'rejected'
      affiliateTier: null, // 1-5, set by admin on approval
      createdAt: new Date(),
      updatedAt: new Date()
    });
    
    return user;
  };

  // Sign in existing user
  const login = async (email, password) => {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    return userCredential.user;
  };

  // Sign out
  const logout = async () => {
    await signOut(auth);
  };

  // Update user data (for refreshing after changes)
  const refreshUserData = async () => {
    if (currentUser) {
      const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
      if (userDoc.exists()) {
        setUserData(userDoc.data());
      }
    }
  };

  // Apply to become affiliate (update user document)
  const applyAsAffiliate = async (applicationData) => {
    if (!currentUser) throw new Error('Not authenticated');
    
    await updateDoc(doc(db, 'users', currentUser.uid), {
      isAffiliate: true,
      appliedAffiliate: true,
      affiliateStatus: 'pending',
      affiliateApplication: applicationData,
      appliedAt: new Date(),
      updatedAt: new Date()
    });
    
    await refreshUserData();
  };

  // Value to be provided to consumers
  const value = {
    currentUser,
    userData,
    loading,
    isAuthenticated: !!currentUser,
    isAffiliate: userData?.isAffiliate === true,
    isApproved: userData?.affiliateStatus === 'approved',
    isPending: userData?.affiliateStatus === 'pending',
    isRejected: userData?.affiliateStatus === 'rejected',
    hasApplied: userData?.affiliateStatus !== null && userData?.affiliateStatus !== undefined,
    affiliateStatus: userData?.affiliateStatus,
    affiliateTier: userData?.affiliateTier || null,
    // Auth methods
    signUp,
    login,
    logout,
    refreshUserData,
    applyAsAffiliate
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
